import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authAPI } from '../lib/api';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const logout = useCallback(async () => {
        // Best-effort server-side logout (revoke refresh tokens)
        try {
            await api.post('/auth/logout');
        } catch {
            // Ignore errors - we're logging out anyway
        }
        localStorage.removeItem('prixdeal_token');
        localStorage.removeItem('prixdeal_user');
        localStorage.removeItem('prixdeal_refresh_token');
        setUser(null);
        window.dispatchEvent(new Event('auth-changed'));
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('prixdeal_token');
        const savedUser = localStorage.getItem('prixdeal_user');
        
        if (token && savedUser) {
            const parsed = JSON.parse(savedUser);
            setUser(parsed);
            const verifyFn = parsed.role === 'supplier' ? authAPI.getSupplierMe : authAPI.getMe;
            verifyFn()
                .then(res => {
                    setUser(res.data);
                    localStorage.setItem('prixdeal_user', JSON.stringify(res.data));
                })
                .catch(() => {
                    // The interceptor handles refresh automatically
                    // If even refresh fails, it clears localStorage
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, []);

    const login = useCallback(async (email, password) => {
        const response = await authAPI.login({ email, password });
        const { token, refresh_token, user: userData } = response.data;
        localStorage.setItem('prixdeal_token', token);
        if (refresh_token) localStorage.setItem('prixdeal_refresh_token', refresh_token);
        localStorage.setItem('prixdeal_user', JSON.stringify(userData));
        setUser(userData);
        window.dispatchEvent(new Event('auth-changed'));
        return userData;
    }, []);

    const loginSupplier = useCallback(async (email, password) => {
        const response = await authAPI.loginSupplier({ email, password });
        const { token, refresh_token, user: userData } = response.data;
        localStorage.setItem('prixdeal_token', token);
        if (refresh_token) localStorage.setItem('prixdeal_refresh_token', refresh_token);
        localStorage.setItem('prixdeal_user', JSON.stringify(userData));
        setUser(userData);
        window.dispatchEvent(new Event('auth-changed'));
        return userData;
    }, []);

    const register = useCallback(async (data) => {
        const response = await authAPI.register(data);
        const { token, refresh_token, user: userData } = response.data;
        localStorage.setItem('prixdeal_token', token);
        if (refresh_token) localStorage.setItem('prixdeal_refresh_token', refresh_token);
        localStorage.setItem('prixdeal_user', JSON.stringify(userData));
        setUser(userData);
        window.dispatchEvent(new Event('auth-changed'));
        return userData;
    }, []);

    const updateUser = useCallback((updatedData) => {
        setUser(prev => {
            const newUser = { ...prev, ...updatedData };
            localStorage.setItem('prixdeal_user', JSON.stringify(newUser));
            return newUser;
        });
    }, []);

    const isAdmin = user?.role === 'admin';
    const isSupplier = user?.role === 'supplier';
    const isAuthenticated = !!user;

    const value = useMemo(() => ({
        user,
        loading,
        login,
        loginSupplier,
        register,
        logout,
        updateUser,
        isAdmin,
        isSupplier,
        isAuthenticated,
    }), [user, loading, login, loginSupplier, register, logout, updateUser, isAdmin, isSupplier, isAuthenticated]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
